import { makeStyles } from '@material-ui/core'
import { getObjectLabels } from '@paintscout/util/builder'
import React, { useState } from 'react'
import Button from '../components/Button'
import { useClientOptions } from '../providers/ClientOptionsProvider'
import type { DialogProps } from '../components/Dialog'
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../components/Dialog'
import Grid from '../components/Grid'
import SelectableChipLarge from '../components/SelectableChip/SelectableChipLarge'
import Typography from '../components/Typography'
import type { SearchContactDealsQuery } from '@paintscout/api'
import { SearchContactDealsDocument } from '@paintscout/api'
import type { Contact } from 'paintscout'
import { useApolloClient } from '@apollo/react-hooks'
import Spinner from '../components/Spinner'

// Copied from pld.ts, keep in sync
export interface PLDDeal {
  id: number
  name: string
  summary: string
  value: number
  person_ids: number[]
  company_id: number
  deal_stage_id: number
  created_at: string
  updated_at: string
  user_id: number
  company?: {
    id: number
    name: string
  }
  deal_stage: {
    id: number
    name: string
    percent: number
  }
  deal_pipeline: {
    id: number
    name: string
  }
}

export interface SelectDealDialogProps extends DialogProps {
  deals: PLDDeal[]
  contact: Contact
  onConfirm: (dealId: string) => void
  onCancel: (event: React.MouseEvent<HTMLElement>) => void
}

const useStyles = makeStyles((theme) => ({
  deals: {
    padding: '2.5px'
  },
  pagination: {
    padding: '2.5px',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(0),
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '10px'
  }
}))

function SelectDealDialog({ onCancel, onConfirm, deals, contact, ...otherProps }: SelectDealDialogProps) {
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const [selectedDealId, setSelectedDealId] = useState(null)
  const classes = useStyles(otherProps)
  const apolloClient = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [allDeals, setAllDeals] = useState<PLDDeal[]>(deals)
  const [page, setPage] = useState(1)

  async function handleConfirm(_ev: any) {
    onConfirm(selectedDealId)
  }

  async function fetchPage(page: number) {
    // check if we already have this page, make sure we dont refetch the last page,
    // as it may be incomplete
    const startIndex = (page - 1) * 50
    if (allDeals.length >= startIndex + 50 || allDeals.length % 50 !== 0) {
      return
    }

    const deals = await apolloClient
      .query<SearchContactDealsQuery>({
        query: SearchContactDealsDocument,
        variables: { contactSourceId: contact.source_id, page },
        fetchPolicy: 'no-cache'
      })
      .then((res) => res.data?.searchContactDeals?.results ?? [])
    setAllDeals((prevDeals) => [...prevDeals, ...deals])
  }
  return (
    <Dialog open={true} onClose={onCancel}>
      <DialogTitle>Choose Pipeline Deal ID</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} justifyContent="space-between" direction="column">
          <Grid item xs={12}>
            <Typography variant={'body1'}>
              We've identified multiple deals associated with this contact. Please select the deal you wish to link to
              this {objectLabels.quote.value.toLowerCase()}.
            </Typography>
          </Grid>
          <Grid item xs={12} style={{ maxWidth: '700px' }}>
            <Grid container justifyContent="space-between" direction="column">
              {loading ? (
                <Grid item>
                  <Spinner />
                </Grid>
              ) : error ? (
                <Grid item>
                  <Typography variant="body1" color="error">
                    {error}
                  </Typography>
                </Grid>
              ) : (
                <>
                  {allDeals.slice((page - 1) * 50, page * 50).map((deal) => {
                    const percent = deal?.deal_stage?.percent ?? 1
                    const disabled = percent === 100 || percent === 0
                    return (
                      <Grid item className={classes.deals} key={deal.id}>
                        <SelectableChipLarge
                          label={`${deal.name}`}
                          sublabel={`${deal.id} - ${deal?.deal_stage?.name ?? 'No Stage'}`}
                          selected={deal.id === selectedDealId}
                          disabled={disabled}
                          onClick={() => {
                            setSelectedDealId(deal.id)
                          }}
                        />
                      </Grid>
                    )
                  })}
                  <Grid item className={classes.deals}>
                    <SelectableChipLarge
                      label={`No Deal`}
                      sublabel={``}
                      selected={selectedDealId === 'none'}
                      onClick={() => {
                        setSelectedDealId('none')
                      }}
                    />
                  </Grid>
                  {(allDeals.length >= page * 50 || allDeals.length > 50) && (
                    <Grid item className={classes.pagination}>
                      <Button
                        variant="textNoPadding"
                        onClick={async () => {
                          setLoading(true)
                          const nextPage = page - 1
                          setPage(nextPage)
                          try {
                            await fetchPage(nextPage)
                          } catch (e) {
                            console.log(e)
                            setError('Error fetching more deals')
                          }
                          setLoading(false)
                        }}
                      >
                        {`< Previous`}
                      </Button>
                      {allDeals.length >= page * 50 && (
                        <Button
                          variant="textNoPadding"
                          onClick={async () => {
                            setLoading(true)
                            setError(null)
                            const nextPage = page + 1
                            setPage(nextPage)
                            try {
                              await fetchPage(nextPage)
                            } catch (e) {
                              console.log(e)
                              setError('Error fetching more deals')
                            }
                            setLoading(false)
                          }}
                        >
                          {`Next >`}
                        </Button>
                      )}
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
      >
        <Button disabled={!selectedDealId} onClick={handleConfirm} variant={'contained'}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SelectDealDialog
